'use client';

import { useScrollPaddingTop } from '@shared/utils';
import { useCallback, useEffect, useRef } from 'react';
import type { MonthKey } from '../types';

type UseMonthVisibilityObserverOptions = {
  last12Months: MonthKey[];
  onVisibleMonthChange: (monthKey: MonthKey) => void;
};

// Single observer shared across all MonthSections so all entries arrive in one batch,
// avoiding per-month callback races. Children register via the returned callback;
// pending registrations are flushed once the observer is created, handling the
// child-before-parent effect order.
function useMonthVisibilityObserver({ last12Months, onVisibleMonthChange }: UseMonthVisibilityObserverOptions) {
  const stickyOffset = useScrollPaddingTop();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingRegistrationsRef = useRef<Element[]>([]);

  const registerWithObserver = useCallback((el: Element): (() => void) => {
    if (observerRef.current) {
      observerRef.current.observe(el);
    } else {
      pendingRegistrationsRef.current.push(el);
    }
    return () => {
      observerRef.current?.unobserve(el);
      pendingRegistrationsRef.current = pendingRegistrationsRef.current.filter(e => e !== el);
    };
  }, []);

  useEffect(() => {
    const visibleMonths = new Set<string>();
    let lastEmittedMonth: string | null = null;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const key = entry.target.getAttribute('data-month-key');
          if (!key) return;
          if (entry.isIntersecting) visibleMonths.add(key);
          else visibleMonths.delete(key);
        });
        const topmost = last12Months.find(k => visibleMonths.has(k));
        if (topmost && topmost !== lastEmittedMonth) {
          lastEmittedMonth = topmost;
          onVisibleMonthChange(topmost);
        }
      },
      { rootMargin: `-${stickyOffset}px 0px 0px 0px` }
    );

    observerRef.current = observer;
    pendingRegistrationsRef.current.forEach(el => observer.observe(el));
    pendingRegistrationsRef.current = [];

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [onVisibleMonthChange, last12Months, stickyOffset]);

  return { registerWithObserver };
}

export { useMonthVisibilityObserver };
